// DTGD-1780: regras de telefone/celular. Sem imports para poder ser copiado
// para o back quando a validação for replicada lá.

// DDDs válidos da ANATEL
const DDDS = new Set(
  [
    [11, 19],
    [21, 22],
    [24, 24],
    [27, 28],
    [31, 35],
    [37, 38],
    [41, 49],
    [51, 51],
    [53, 55],
    [61, 69],
    [71, 71],
    [73, 75],
    [77, 77],
    [79, 79],
    [81, 89],
    [91, 99],
  ].flatMap(([from, to]) =>
    Array.from({ length: to - from + 1 }, (_, i) => String(from + i)),
  ),
);

export const PHONE_MESSAGES = {
  required: 'Campo obrigatório',
  length: 'Informe DDD + número: (DD) XXXX-XXXX ou (DD) 9XXXX-XXXX',
  repeated: 'Número inválido: todos os dígitos são iguais',
  ddd: 'DDD inválido',
  mobile: 'Celular deve começar com 9 após o DDD: (DD) 9XXXX-XXXX',
  notMobile: 'Informe um celular com DDD: (DD) 9XXXX-XXXX',
  valid: 'Telefone válido',
};

export const onlyDigits = value => String(value ?? '').replace(/\D/g, '');

export const isValidDDD = ddd => DDDS.has(ddd);

const isMobile = digits => digits[2] === '9';

const MAX_DIGITS = 11;

// Normaliza o que foi digitado/colado: tira o 0 de operadora e o +55 do país
// e limita a 11 dígitos (fixo com 11 fica para a validação apontar o erro).
// O 0 só sai quando sobra um número completo, para não sumir com "000...".
// O 55 só sai quando não pode ser um DDD 55 digitado com um dígito a mais
// (+ explícito, 13 dígitos, ou 12 formando um fixo).
export function toPhoneDigits(value) {
  const stripTrunk = d => {
    const rest = d.replace(/^0+/, '');
    return rest.length >= 10 ? rest : d;
  };
  let digits = stripTrunk(onlyDigits(value));
  const rest = digits.slice(2);
  const hasCountryCode =
    digits.startsWith('55') &&
    (/^\s*\+/.test(String(value ?? '')) ||
      digits.length === 13 ||
      (digits.length === 12 && !isMobile(rest)));
  if (hasCountryCode) digits = stripTrunk(rest);
  return digits.slice(0, MAX_DIGITS);
}

// mobileOnly: recusa fixo (ex.: celular que recebe SMS).
// required: false aceita vazio, mas valida quando preenchido.
export function validatePhone(
  value,
  { mobileOnly = false, required = true } = {},
) {
  const digits = onlyDigits(value);
  const invalid = reason => ({ valid: false, reason });

  if (!digits)
    return required ? invalid('required') : { valid: true, reason: null };
  if (digits.length !== 10 && digits.length !== 11) return invalid('length');
  if (/^(\d)\1+$/.test(digits)) return invalid('repeated');
  if (!isValidDDD(digits.slice(0, 2))) return invalid('ddd');
  if (digits.length === 11 && !isMobile(digits)) return invalid('mobile');
  if (digits.length === 10 && isMobile(digits)) return invalid('length');
  if (mobileOnly && digits.length !== 11) return invalid('notMobile');

  return { valid: true, reason: null };
}

export function phoneMessage(value, options) {
  const { valid, reason } = validatePhone(value, options);
  return valid ? PHONE_MESSAGES.valid : PHONE_MESSAGES[reason];
}

// Fixo: (DD) XXXX-XXXX. Celular (9 após o DDD) ou 11 dígitos: (DD) XXXXX-XXXX.
// Mais de 11 dígitos (só em dado legado) aparece cru, sem esconder dígitos.
export function formatPhone(value) {
  const digits = onlyDigits(value);
  if (!digits) return '';
  if (digits.length > MAX_DIGITS) return digits;

  const ddd = digits.slice(0, 2);
  const number = digits.slice(2);
  if (!number) return `(${ddd}`;

  const split = isMobile(digits) || digits.length === MAX_DIGITS ? 5 : 4;
  const prefix = number.slice(0, split);
  const suffix = number.slice(split);

  return `(${ddd}) ${prefix}${suffix ? `-${suffix}` : ''}`;
}
